/* eslint-disable react/destructuring-assignment */
import { Avatar, Badge, Drawer, Dropdown, Select, Spin } from "antd";
import { useMutation } from "@apollo/client";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserContext } from "../../amplify/authenticator-provider";
import useLang from "../../hooks/useLang";
import { getEffectiveCognitoRoles, isPlatformAdmin } from "../../hooks/utils";
import { useDevice } from "../../hooks/useDevice";
import NotificationIcon from "../../icons/js/NotificationIcon";
import {
  pushActiveLeadFilter,
  resetApiCacheStore,
  setSelectedLeadPhone,
} from "../../redux/slices/apiCacheSlice";
import { setDrawerStatus } from "../../redux/slices/drawerSlice";
import { getMobileView } from "../../redux/slices/isMobile";
import { getLocationId, setLocationIdSlice } from "../../redux/slices/locationSlice";
import AntdIconV4 from "../../utils/antdIcon";
import { dynamicSort } from "../../utils/dataSorting";
import NavigationContainer, {
  SIDEBAR_BG,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from "../NavigationContainer/navigationContainer";
import Toast from "../toast/Toast";
import "./layout.css";
import {
  NotificationsDropdown,
  convertToInitials,
  getPageTitle,
  profileMenu,
} from "./layoutHelpers";
import { addQuickFilter, QUICK_FILTERS, resetLeadFilters } from "../../redux/slices/leadSlice";
import { resetOpportunityFilters } from "../../redux/slices/opportunitySlice";
import { checkAccessPermission } from "../../library/helpers";
import { AccessPermissionAction } from "../../library/constants";
import { clearAllPersistedFilters } from "../../redux/slices/persistedFiltersSlice";
import { isAdminRoute, isReportRouteMatch } from "../../utils/ReportUtils";
import { setTasks } from "../../redux/slices/taskSlice";
import { setNotesRedux } from "../../redux/slices/notesSlice";
import {
  isImpersonating,
  getImpersonatedUserLabel,
  stopImpersonation,
} from "../../helpers/impersonation";
import { STOP_IMPERSONATION } from "../../graphql/mutation";
import AnnouncementBanner from "./announcementBanner";

const MainLayout = props => {
  const {
    accessPayload,
    permissionLeader,
    list,
    checkValue,
    autoDeleteTime,
    setSocketMsgSelectedLead,
    setPhone,
    location2,
    setLocation,
    getActiveCrmIntegration,
    setUser,
    persistor,
    currentPath,
    user,
    setchanged,
    changed,
    notificationsCount,
    children,
    setShowSupportModal,
    deleteFcmDevice,
    userFullName,
    setContentStyle,
    refetchNotificationsCount,
  } = props;

  const dispatch = useDispatch();
  const [lang] = useLang();
  const isMobileView = useSelector(state => getMobileView(state));
  const locationIdslice = useSelector(state => getLocationId(state));
  const [bindedIsFullScreen, setBindedIsFullScreen] = useState(true);
  const { isFullScreen, setIsFullScreen, authData } = useContext(UserContext);
  const [notificationDrawer, setNotificationDrawer] = useState(false);
  // Rail starts collapsed everywhere; the user expands it when they want it.
  // Kept as state, not derived, so that choice sticks.
  const { isXlUp } = useDevice();
  const [navCollapsed, setNavCollapsed] = useState(true);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);

  const isPlatformAdminUser = isPlatformAdmin(authData);

  // EAI-1486: end impersonation. Fire STOP_IMPERSONATION first (the header is
  // still set, so the backend resolves request.impersonator and writes the
  // stop audit entry), then clear + reload regardless of the result so a
  // failed audit call never traps the admin in the impersonated session.
  const [stopImpersonationMutation] = useMutation(STOP_IMPERSONATION);
  const handleExitImpersonation = async () => {
    try {
      await stopImpersonationMutation();
    } catch (e) {
      // best-effort audit; proceed to exit either way
    } finally {
      stopImpersonation();
    }
  };

  useEffect(() => {
    if (isMobileView) {
      if (window.location.pathname.includes("activity-center")) {
        setIsFullScreen(false);
      } else {
        setIsFullScreen(true);
      }
    }
  }, [window.location.href]);

  useEffect(() => {
    isMobileView && setBindedIsFullScreen(isFullScreen);
  }, [isFullScreen]);

  // Force the rail shut when the viewport drops below xl, where an expanded
  // 200px rail eats too much of the content column. Deliberately one-way: it
  // never auto-expands, so the collapsed default and any manual choice stand.
  useEffect(() => {
    if (!isXlUp) {
      setNavCollapsed(true);
      setContentStyle(false);
    }
  }, [isXlUp]);

  const handleNotificationDrawer = () => {
    setNotificationDrawer(prev => !prev);
  };

  const setSelectedLeadPhoneToRedux = selectedLeadPhone => {
    dispatch(setSelectedLeadPhone({ selectedLeadPhone }));
  };

  const handleActiveLeadFilterOnLogout = () => {
    dispatch(pushActiveLeadFilter({ activeLeadFilter: {} }));
  };

  const resetLeadFilterOnLogout = () => {
    dispatch(resetLeadFilters());
  };

  // `checkAccessPermission` reads context, so it must be evaluated during
  // render — not inside the location-change handler.
  const restrictRemoveDefaultFilter = checkAccessPermission(
    AccessPermissionAction.ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER
  );

  const setLocationToNullOnLogout = () => {
    dispatch(setLocationIdSlice({ locationId: null }));
  };

  // Route-driven layout toggles
  const activePath = typeof window !== "undefined" ? window.location.pathname : "";
  // Admin routes render without the product sidebar.
  const hideSideNav = isAdminRoute();
  const showHeader = (isMobileView && isFullScreen) || !isMobileView;
  const pageTitle = getPageTitle(currentPath);

  const navProps = { setContentStyle, accessPayload };

  return (
    <div className="min-h-screen">
      {/* Announcement copy follows the logged-in user's own
          `preferredLanguageCode` (ME query). */}
      <AnnouncementBanner languageCode={user?.preferredLanguageCode} />
      {isImpersonating() && (
        <div className="sticky top-0 z-[60] flex items-center justify-center gap-3 bg-amber-500 px-3 py-1.5 text-sm font-medium text-white">
          <span>
            Impersonating <strong>{getImpersonatedUserLabel() || "another user"}</strong> — you are
            viewing the app with this user&apos;s exact permissions.
          </span>
          <button
            type="button"
            className="rounded-[100px] bg-white/20 px-3 py-0.5 hover:bg-white/30"
            onClick={() => handleExitImpersonation()}
          >
            Exit impersonation
          </button>
        </div>
      )}
      <Spin
        spinning={
          getEffectiveCognitoRoles(accessPayload)[0] !== "ADMIN" &&
          permissionLeader &&
          !isReportRouteMatch(window.location.pathname) &&
          !window.location.pathname.includes("/call-detail/")
        }
        className="fixed top-[50%] left-[50%]"
        style={{
          transform: "translateX(-50%) translateY(-50%)",
        }}
      />

      <div className="flex min-h-screen">
        {/* Full-height rail. Visibility is driven by isMobileView (<=800px)
            alone — a Tailwind md: guard here would leave a 769-800px dead zone
            with neither the rail nor the mobile bar. */}
        {!hideSideNav && !isMobileView && (
          <div
            className="sticky top-0 block h-screen shrink-0 transition-[width] duration-150"
            style={{
              width: navCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
              backgroundColor: SIDEBAR_BG,
            }}
          >
            <NavigationContainer
              {...navProps}
              collapsed={navCollapsed}
              setCollapsed={setNavCollapsed}
            />
          </div>
        )}

        {/* Header + content share the remaining width. */}
        <div className="flex min-w-0 flex-1 flex-col">
          {showHeader && (
            <div className="sticky top-0 z-50 flex h-14 w-full items-center justify-between gap-2 bg-white px-3 py-1">
              <div className="flex min-w-0 items-center gap-2">
                {isMobileView &&
                  (window.location.pathname.includes("settings") ||
                    window.location.pathname.includes("admin")) && (
                    <AntdIconV4
                      type="MenuOutlined"
                      onClick={() => dispatch(setDrawerStatus({ open: true }))}
                      className="cursor-pointer text-2xl flex-1 font-semibold"
                    />
                  )}
                {pageTitle && (
                  <span className="truncate text-base font-medium text-[#0F172A]">{pageTitle}</span>
                )}
                <Toast
                  toastList={list}
                  autoDelete={checkValue}
                  autoDeleteTime={autoDeleteTime}
                  onClickofToast={leadId => setSocketMsgSelectedLead(leadId)}
                  isFromLayout
                />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {getEffectiveCognitoRoles(accessPayload)[0] !== "ADMIN" && (
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    onChange={e => {
                      localStorage.removeItem("changedLocationLeadId");
                      localStorage.removeItem("clickLeadId");
                      setPhone("");
                      if (e === "all") {
                        let ids = "";
                        for (let i = 0; i < location2.length; i++) {
                          const element = location2[i];
                          ids = `${ids}${element.id}${location2.length - 1 === i ? "" : ","}`;
                        }
                        sessionStorage.setItem("currentLocationId", ids);
                        dispatch(setLocationIdSlice({ locationId: ids }));
                      } else {
                        setLocation(e);
                        sessionStorage.setItem("currentLocationId", e);
                        const crmIntegrationType = getActiveCrmIntegration(e);
                        setUser({
                          ...user,
                          locationId: e,
                          company: { ...user.company, crmIntegrationType },
                        });
                      }
                      if (window.location.pathname?.includes("activity-center")) {
                        window.history.pushState("", "", `/engagements/activity-center`);
                        // Clear targetMessageId from sessionStorage on manual location switch.
                        // engagementActivity.js useEffect detects this (locationIdslice change + no sessionStorage)
                        // and clears the state, which hides the "View all messages" button and
                        // ensures messagesV2 fetches normal paginated messages for the new location.
                        sessionStorage.removeItem("targetMessageId");
                      }
                      dispatch(setLocationIdSlice({ locationId: e }));
                      persistor.pause();
                      persistor
                        .flush()
                        .then(() => persistor.purge())
                        .then(() => persistor.persist());
                      dispatch(setTasks({ tasks: [], count: 0 }));
                      dispatch(setNotesRedux([]));
                      dispatch(resetApiCacheStore({}));
                      dispatch(clearAllPersistedFilters());
                      // `persistor.purge()` only wipes what's in storage — the
                      // in-memory `state.lead.filters` survives and gets re-persisted
                      // on the next write. Those filters hold company/location-scoped
                      // ids (opportunity type/status/source, users, teams) plus their
                      // chip counts, so carrying them into a new company left the
                      // panel showing counts for selections that no longer exist and
                      // filtered the lead list down to nothing. Reset explicitly.
                      dispatch(resetLeadFilters());
                      // Lead Center's Opportunity view keeps its filters in a
                      // separate slice — type/status/source, owners, teams and
                      // the date window are all location-scoped, so they'd
                      // otherwise carry ids that don't exist in the new
                      // location. Reset in the SAME batch as the lead filters
                      // so both land in one render (one refetch, not two).
                      // MY_CONVERSATIONS is unaffected: it lives in the lead
                      // slice and is re-applied from the permission below.
                      dispatch(resetOpportunityFilters());
                      // Re-apply the forced default in this SAME batch. The activity
                      // center would otherwise re-add it a tick later
                      // (setFilterStatusForTeamLeader), and that clear-then-re-add
                      // is two separate filter states — so getLeads ran once without
                      // MY_CONVERSATIONS and again with it, the first being
                      // cancelled mid-flight. React 18 batches this dispatch with
                      // the reset above, so the filters land in their final state in
                      // a single render and only the correct request goes out.
                      if (restrictRemoveDefaultFilter) {
                        dispatch(addQuickFilter(QUICK_FILTERS.MY_CONVERSATIONS));
                      }
                      setchanged(!changed);
                    }}
                    value={
                      location2?.length > 0
                        ? sessionStorage.getItem("currentLocationId")?.includes(",")
                          ? "all"
                          : sessionStorage.getItem("currentLocationId")?.toString().trim()
                        : ""
                    }
                    className="min-w-[110px] sm:min-w-[160px] lg:min-w-[200px]"
                  >
                    {(location2 || []).length > 1 && (
                      <Select.Option key="all" value="all">
                        {lang.all}
                      </Select.Option>
                    )}
                    {((location2 || []).sort(dynamicSort("title")) || []).map(item => (
                      <Select.Option
                        key={item?.id?.toString().trim()}
                        value={item?.id?.toString().trim()}
                      >
                        {item.title}
                      </Select.Option>
                    ))}
                  </Select>
                )}

                <div className="mr-3">
                  <Badge
                    count={notificationsCount ? notificationsCount.getUnreadNotificationsCount : 0}
                  >
                    <NotificationIcon
                      onClick={() => setNotificationDrawer(true)}
                      className="text-[24px] cursor-pointer mx-2 mt-[5px] text-[#64748B]"
                    />
                  </Badge>
                </div>
                <Drawer
                  title="Notifications"
                  open={notificationDrawer}
                  className="notification-drawer"
                  width={isMobileView ? "100%" : "30%"}
                  onClose={() => setNotificationDrawer(false)}
                >
                  <NotificationsDropdown
                    setSelectedLeadPhoneToRedux={setSelectedLeadPhoneToRedux}
                    locationIdslice={locationIdslice}
                    refetchNotificationsCount={refetchNotificationsCount}
                    onClose={() => setNotificationDrawer(false)}
                    showDrawer={notificationDrawer}
                    unreadCount={notificationsCount?.getUnreadNotificationsCount ?? 0}
                  />
                </Drawer>
                <div className="flex items-center gap-3">
                  <Dropdown
                    overlay={profileMenu(
                      setShowSupportModal,
                      deleteFcmDevice,
                      handleActiveLeadFilterOnLogout,
                      resetLeadFilterOnLogout,
                      setLocationToNullOnLogout,
                      isPlatformAdminUser,
                      persistor,
                      dispatch,
                      null,
                      {},
                      null,
                      user?.id
                    )}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <div className="bg-[#F1F5F9] flex items-center p-1 pr-3 rounded-[100px] cursor-pointer">
                      <Avatar src={user.profilePic} size="small" className="mr-2">
                        {convertToInitials(userFullName)}
                      </Avatar>
                      {!isMobileView && <span className="text-sm">{userFullName}</span>}
                    </div>
                  </Dropdown>
                </div>
              </div>
            </div>
          )}

          {/* Mobile nav bar. Rendered outside the header on purpose: the header
              is hidden when isFullScreen is false (e.g. activity-center), and
              this stays the way to reach navigation there. Opens the drawer. */}
          {isMobileView && !hideSideNav && (
            <div
              onClick={() => setNavDrawerOpen(true)}
              className="primaryBgTheme w-full cursor-pointer py-1 text-center text-sm text-white"
            >
              {pageTitle}
            </div>
          )}

          <div
            className={`flex-1 contentContainer min-w-0 ${!isMobileView && "py-3 pr-[6px] pl-2"} ${
              isReportRouteMatch(activePath) || activePath.includes("admin-dashboard")
                ? "!min-h-fit"
                : ""
            }`}
            style={{
              // --announcement-height is published by AnnouncementBanner (0px
              // when hidden/dismissed) so the content column never overflows.
              height: `calc(100vh - ${isMobileView ? 40 : 56}px - var(--announcement-height, 0px))`,
            }}
            id="mainLayout"
          >
            {children}
          </div>
        </div>
      </div>

      {/* Mobile off-canvas navigation */}
      {!hideSideNav && (
        <Drawer
          placement="left"
          open={navDrawerOpen}
          onClose={() => setNavDrawerOpen(false)}
          width={SIDEBAR_EXPANDED_WIDTH + 40}
          closable={false}
          styles={{ body: { padding: 0, backgroundColor: SIDEBAR_BG } }}
        >
          <NavigationContainer
            {...navProps}
            variant="drawer"
            setCollapsed={setNavCollapsed}
            onNavigate={() => setNavDrawerOpen(false)}
          />
        </Drawer>
      )}
    </div>
  );
};

export default MainLayout;
